import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, CheckCircle2, ExternalLink, Megaphone, Radar, Search, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { toast } from 'sonner';
import GoogleAdsControlPanel from './GoogleAdsControlPanel';

const lines = (value) => asArray(value).join('\n');
const values = (value) => String(value || '').split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
const formatNumber = (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;
const formatPosition = (value) => Number(value || 0) ? Number(value).toFixed(1) : '—';

function Metric({ label, value }) {
  return <div className="rounded-lg bg-secondary/20 border border-white/5 p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-heading font-bold mt-1">{value}</div></div>;
}

function SearchRows({ title, rows, field }) {
  const items = asArray(rows).slice(0, 5);
  return (
    <div className="rounded-lg bg-secondary/20 border border-white/5 p-3">
      <div className="text-sm font-medium mb-2">{title}</div>
      {items.length === 0 ? <div className="text-xs text-muted-foreground">No Google data loaded yet.</div> : items.map((item) => (
        <div key={item[field]} className="flex items-center justify-between gap-3 py-1.5 text-xs border-b border-white/5 last:border-0">
          <span className="truncate" title={item[field]}>{item[field]}</span>
          <span className="text-muted-foreground whitespace-nowrap">{formatNumber(item.clicks)} clicks · {formatNumber(item.impressions)} views</span>
        </div>
      ))}
    </div>
  );
}

function initialForm(profile, app) {
  return {
    source_urls: lines(profile?.source_urls?.length ? profile.source_urls : [app.base_url]),
    primary_conversion: profile?.primary_conversion || 'qualified_lead',
    target_markets: lines(profile?.target_markets),
    target_languages: lines(profile?.target_languages?.length ? profile.target_languages : ['English']),
    approved_keywords: lines(profile?.approved_keywords),
    negative_keywords: lines(profile?.negative_keywords),
    approved_cta: profile?.approved_cta || '',
    daily_ad_budget: ((profile?.daily_ad_budget_cents || 0) / 100).toFixed(2),
  };
}

function ToggleControl({ label, detail, paused, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg bg-secondary/20 border border-white/5 p-3">
      <div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!paused)}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${paused ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}
        data-testid={testId}
      >
        {paused ? 'Paused' : 'Allowed'}
      </button>
    </div>
  );
}

export default function AppGrowthPanel({ app }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(() => initialForm(null, app));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [searchConsole, setSearchConsole] = useState(null);
  const [searchConsoleBusy, setSearchConsoleBusy] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState(`${app.base_url.replace(/\/$/, '')}/sitemap.xml`);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [growthRes, searchConsoleRes] = await Promise.all([
        api.get(`/apps/${app.id}/growth`),
        api.get(`/apps/${app.id}/growth/search-console/status`),
      ]);
      setProfile(growthRes.data);
      setForm(initialForm(growthRes.data, app));
      setSearchConsole(searchConsoleRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Growth controls');
    } finally {
      setLoading(false);
    }
  }, [app]);

  useEffect(() => { load(); }, [load]);

  const payload = useMemo(() => ({
    source_urls: values(form.source_urls),
    primary_conversion: form.primary_conversion.trim() || null,
    target_markets: values(form.target_markets),
    target_languages: values(form.target_languages),
    approved_keywords: values(form.approved_keywords),
    negative_keywords: values(form.negative_keywords),
    approved_cta: form.approved_cta.trim() || null,
    daily_ad_budget_cents: Math.max(0, Math.round((Number(form.daily_ad_budget) || 0) * 100)),
  }), [form]);

  const save = async (event) => {
    event?.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/apps/${app.id}/growth`, payload);
      setProfile(res.data);
      setForm(initialForm(res.data, app));
      toast.success('Growth plan saved — advertising is still off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save Growth plan');
    } finally {
      setSaving(false);
    }
  };

  const patchControl = async (field, value) => {
    try {
      const res = await api.put(`/apps/${app.id}/growth`, { [field]: value });
      setProfile(res.data);
      toast.success('Safety control updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update safety control');
    }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/scan`, { source_urls: payload.source_urls });
      setProfile(res.data);
      setForm(initialForm(res.data, app));
      toast.success('Website scan complete — no AI or ads were run');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Website scan failed');
    } finally {
      setScanning(false);
    }
  };

  const connectSearchConsole = async () => {
    setSearchConsoleBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/search-console/connect`);
      window.location.assign(res.data.authorization_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the Google connection');
      setSearchConsoleBusy(false);
    }
  };

  const chooseSearchProperty = async (siteUrl) => {
    setSearchConsoleBusy(true);
    try {
      const res = await api.put(`/apps/${app.id}/growth/search-console/property`, { site_url: siteUrl });
      setSearchConsole(res.data);
      toast.success('Search Console property selected for this app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not select that property');
    } finally {
      setSearchConsoleBusy(false);
    }
  };

  const syncSearchConsole = async () => {
    setSearchConsoleBusy(true);
    try {
      const res = await api.post(`/apps/${app.id}/growth/search-console/sync`);
      setSearchConsole(res.data);
      toast.success('Google Search performance updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update Search Console');
    } finally {
      setSearchConsoleBusy(false);
    }
  };

  const submitSitemap = async () => {
    setSearchConsoleBusy(true);
    try {
      await api.post(`/apps/${app.id}/growth/search-console/sitemap`, { sitemap_url: sitemapUrl.trim() });
      toast.success('Sitemap submitted to Google');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit this sitemap');
    } finally {
      setSearchConsoleBusy(false);
    }
  };

  const disconnectSearchConsole = async () => {
    if (!window.confirm(`Disconnect Google Search Console from ${app.name}? This does not affect the Google property itself.`)) return;
    setSearchConsoleBusy(true);
    try {
      await api.delete(`/apps/${app.id}/growth/search-console`);
      setSearchConsole({ configured: true, connected: false, available_sites: [], metrics: {} });
      setProfile((current) => ({ ...current, search_console_connected: false }));
      toast.success('Search Console disconnected from this app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not disconnect Search Console');
    } finally {
      setSearchConsoleBusy(false);
    }
  };

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Loading Growth Center…</div>;
  if (!profile) return <div className="py-10 text-center text-sm text-muted-foreground">Growth controls unavailable.</div>;

  const checks = asArray(profile.last_scan_summary).flatMap((page) => asArray(page.search_checks));
  const passingChecks = checks.filter((check) => check.ok).length;

  return (
    <div className="space-y-5" data-testid="app-growth-panel">
      <div className="glass rounded-xl border border-primary/20 p-5">
        <div className="flex items-start gap-3">
          <Radar className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h2 className="font-heading font-bold text-foreground">Growth Center for {app.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">One place to prepare Google Search, AI content, LinkedIn, and Google Ads for this app. Paid campaigns remain approval-controlled and spend-locked.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-2 mt-4 text-xs">
          {['1. Scan website', '2. Approve growth plan', '3. Generate and review posts', '4. Connect measurement before ads'].map((step) => (
            <div key={step} className="rounded-lg bg-secondary/20 border border-white/5 px-3 py-2 text-muted-foreground">{step}</div>
          ))}
        </div>
      </div>

      <div className="glass rounded-xl border border-white/5 p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2"><Search className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold">Website & Google Search</h3></div>
          <button type="button" onClick={scan} disabled={scanning} className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs disabled:opacity-50" data-testid="scan-growth-source-btn">
            {scanning ? 'Scanning…' : 'Scan approved pages'}
          </button>
        </div>
        <label className="block text-xs text-muted-foreground">Approved website pages (same website only, maximum 5)</label>
        <textarea value={form.source_urls} onChange={(e) => setForm({ ...form, source_urls: e.target.value })} rows={3} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-primary/50" data-testid="growth-source-urls" />
        <div className="flex flex-wrap gap-2 text-xs">
          <span className={`px-2 py-1 rounded-full ${profile.last_scan_status === 'succeeded' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-secondary/40 text-muted-foreground'}`}>Scan: {profile.last_scan_status || 'not run'}</span>
          {checks.length > 0 && <span className="px-2 py-1 rounded-full bg-secondary/40 text-muted-foreground">Search checks: {passingChecks}/{checks.length}</span>}
          <span className="px-2 py-1 rounded-full bg-secondary/40 text-muted-foreground">Search Console: {profile.search_console_connected ? 'connected' : 'not connected'}</span>
        </div>
        {profile.last_scan_error && <p className="text-xs text-destructive">{profile.last_scan_error}</p>}
        {asArray(profile.last_scan_summary).map((page) => (
          <div key={page.url} className="rounded-lg bg-secondary/20 border border-white/5 p-3">
            <div className="text-sm font-medium text-foreground">{page.title || 'Untitled page'}</div>
            <div className="text-xs font-mono text-primary break-all mt-1">{page.url}</div>
            <div className="grid sm:grid-cols-2 gap-1 mt-2 text-xs text-muted-foreground">
              {asArray(page.search_checks).map((check) => <div key={check.name}>{check.ok ? '✓' : '✗'} {check.detail}</div>)}
            </div>
          </div>
        ))}
      </div>

      <div className="glass rounded-xl border border-white/5 p-5 space-y-4" data-testid="search-console-panel">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><Search className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold">Google Search Console</h3></div>
            <p className="text-xs text-muted-foreground mt-1">See how people find this app in Google. This connection cannot create ads or spend money.</p>
          </div>
          {!searchConsole?.connected ? (
            <button type="button" onClick={connectSearchConsole} disabled={searchConsoleBusy || !searchConsole?.configured} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs disabled:opacity-50" data-testid="connect-search-console">
              {searchConsoleBusy ? 'Opening Google…' : 'Connect Google Search Console'}
            </button>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={syncSearchConsole} disabled={searchConsoleBusy || !searchConsole.selected_site_url} className="px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs disabled:opacity-50" data-testid="sync-search-console">Update results</button>
              <button type="button" onClick={disconnectSearchConsole} disabled={searchConsoleBusy} className="px-3 py-1.5 rounded-lg border border-red-500/20 text-red-300 text-xs disabled:opacity-50">Disconnect</button>
            </div>
          )}
        </div>
        {!searchConsole?.configured && <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-200">Server setup is required once: add the Google Search Console OAuth client. After that, each app connects here.</div>}
        {searchConsole?.connected && (
          <>
            <label className="block text-xs text-muted-foreground">Search Console property for {app.name}</label>
            <div className="flex flex-wrap gap-2" data-testid="search-console-property">
              {asArray(searchConsole.available_sites).length === 0 ? <span className="text-xs text-muted-foreground">No matching website property found in this Google account.</span> : asArray(searchConsole.available_sites).map((site) => (
                <button key={site.site_url} type="button" onClick={() => chooseSearchProperty(site.site_url)} disabled={searchConsoleBusy} className={`px-3 py-2 rounded-lg border text-xs font-mono disabled:opacity-50 ${searchConsole.selected_site_url === site.site_url ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-white/10 bg-secondary/30 text-muted-foreground'}`}>
                  {searchConsole.selected_site_url === site.site_url ? '✓ ' : ''}{site.site_url}
                </button>
              ))}
            </div>
            {searchConsole.last_sync_error && <p className="text-xs text-destructive">{searchConsole.last_sync_error}</p>}
            {searchConsole.selected_site_url && (
              <div className="grid sm:grid-cols-4 gap-2">
                <Metric label="Clicks" value={formatNumber(searchConsole.metrics?.clicks)} />
                <Metric label="Impressions" value={formatNumber(searchConsole.metrics?.impressions)} />
                <Metric label="Click rate" value={formatPercent(searchConsole.metrics?.ctr)} />
                <Metric label="Average position" value={formatPosition(searchConsole.metrics?.position)} />
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
              <SearchRows title="Top searches" rows={searchConsole.metrics?.top_queries} field="query" />
              <SearchRows title="Top pages" rows={searchConsole.metrics?.top_pages} field="page" />
            </div>
            <div className="rounded-lg bg-secondary/20 border border-white/5 p-3 space-y-2">
              <div className="text-sm font-medium">Tell Google about the sitemap</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={sitemapUrl} onChange={(event) => setSitemapUrl(event.target.value)} className="flex-1 bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
                <button type="button" onClick={submitSitemap} disabled={searchConsoleBusy || !searchConsole.selected_site_url || !sitemapUrl.trim()} className="px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs disabled:opacity-50">Submit sitemap</button>
              </div>
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground">{searchConsole?.data_note || 'Search performance is delayed and is not real-time.'}</p>
      </div>

      <form onSubmit={save} className="grid lg:grid-cols-2 gap-5">
        <div className="glass rounded-xl border border-white/5 p-5 space-y-3">
          <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold">Content & LinkedIn plan</h3></div>
          <input value={form.primary_conversion} onChange={(e) => setForm({ ...form, primary_conversion: e.target.value })} placeholder="Primary conversion, e.g. qualified_lead" className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.target_markets} onChange={(e) => setForm({ ...form, target_markets: e.target.value })} placeholder="Target markets, one per line" rows={2} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.target_languages} onChange={(e) => setForm({ ...form, target_languages: e.target.value })} placeholder="Languages, one per line" rows={2} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input value={form.approved_cta} onChange={(e) => setForm({ ...form, approved_cta: e.target.value })} placeholder="Approved call to action" className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <div className="space-y-2">
            {asArray(profile.playbooks).length === 0 ? <p className="text-xs text-muted-foreground">No Campaign Playbook uses this app yet.</p> : asArray(profile.playbooks).map((pb) => (
              <div key={pb.id} className="rounded-lg bg-secondary/20 px-3 py-2 text-xs text-muted-foreground"><span className="text-foreground font-medium">{pb.name}</span> · {pb.paused ? 'paused' : pb.mode} · generation {pb.generation_schedule_enabled ? 'scheduled' : 'manual'}</div>
            ))}
          </div>
          <button type="button" onClick={() => navigate(`/automations?source_app_id=${app.id}`)} className="inline-flex items-center gap-1 text-xs text-primary">Manage campaign playbooks <ExternalLink className="w-3 h-3" /></button>
        </div>

        <div className="glass rounded-xl border border-white/5 p-5 space-y-3">
          <div className="flex items-center gap-2"><Megaphone className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold">Google Ads plan</h3></div>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-200">Ads are locked OFF. Budget and keywords are planning fields only; no campaign can launch or spend money.</div>
          <textarea value={form.approved_keywords} onChange={(e) => setForm({ ...form, approved_keywords: e.target.value })} placeholder="Approved search keywords, one per line" rows={3} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <textarea value={form.negative_keywords} onChange={(e) => setForm({ ...form, negative_keywords: e.target.value })} placeholder="Negative keywords, one per line" rows={3} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <label className="block text-xs text-muted-foreground">Planned daily budget (EUR — does not activate spend)</label>
          <input type="number" min="0" step="0.01" value={form.daily_ad_budget} onChange={(e) => setForm({ ...form, daily_ad_budget: e.target.value })} className="w-full bg-secondary/30 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="growth-daily-budget" />
          <div className="text-xs text-muted-foreground">Google Ads: {profile.google_ads_connected ? 'connected' : 'not connected'} · Conversion tracking: {profile.primary_conversion ? 'planned' : 'not configured'}</div>
        </div>

        <button type="submit" disabled={saving} className="lg:col-span-2 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-medium disabled:opacity-50" data-testid="save-growth-plan-btn">{saving ? 'Saving…' : 'Save Growth plan'}</button>
      </form>

      <GoogleAdsControlPanel app={app} growth={profile} />

      <div className="glass rounded-xl border border-white/5 p-5 space-y-3">
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold">Safety controls for this app</h3></div>
        <ToggleControl label="AI generation" detail="Blocks Run Once and scheduled AI generation for playbooks attached to this app." paused={profile.ai_generation_paused} onChange={(paused) => patchControl('ai_generation_paused', paused)} testId="toggle-app-ai" />
        <ToggleControl label="LinkedIn publishing" detail="Blocks Publish Now and scheduled publishing for campaign posts attached to this app." paused={profile.publishing_paused} onChange={(paused) => patchControl('publishing_paused', paused)} testId="toggle-app-publishing" />
        <div className="flex items-center justify-between rounded-lg bg-secondary/20 border border-white/5 p-3">
          <div><div className="text-sm font-medium">Google Ads live spending</div><div className="text-xs text-muted-foreground mt-0.5">Connection, reporting, and proposal approval are available above. Live activation stays separately locked.</div></div>
          <span className="px-3 py-1.5 rounded-lg text-xs border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">Spend lock on</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Existing previews, pictures, post editing, approvals, schedules, and LinkedIn controls remain unchanged.</div>
      </div>
    </div>
  );
}
